/**
 * Chart option builders.
 *
 * Every chart in the app is one of four forms, and each form is built here so
 * the mark specs stay identical everywhere: smooth 2.5px lines with a gradient
 * area on the lead series, rounded padded donuts with a centre KPI,
 * pill-capped stacked bars separated by 2px surface gaps, and single-hue
 * horizontal bars with a rounded data end. Colour comes from the mode's
 * palette (see chartTheme.ts) — call sites pass data and formatters, not hexes.
 */
import type { EChartsCoreOption } from 'echarts/core';
import { ChartPalette } from './chartTheme';

export type ValueFormatter = (value: number) => string;

const identityFormat: ValueFormatter = (value) => value.toLocaleString();

/** Blend a hex colour with an alpha channel, for area washes and tints. */
export function withAlpha(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const int = parseInt(full, 16);
    // eslint-disable-next-line no-bitwise
    return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}

function areaGradient(color: string) {
    return {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
            { offset: 0, color: withAlpha(color, 0.3) },
            { offset: 1, color: withAlpha(color, 0) },
        ],
    };
}

function baseTooltip(
    palette: ChartPalette,
    trigger: 'axis' | 'item',
    axisPointerType: 'line' | 'shadow' = 'line',
) {
    return {
        trigger,
        confine: true,
        appendToBody: false,
        axisPointer:
            axisPointerType === 'shadow'
                ? { type: 'shadow', shadowStyle: { color: palette.chrome.gridline, opacity: 0.6 } }
                : { type: 'line', lineStyle: { color: palette.chrome.gridline, width: 1, type: 'solid' } },
    };
}

/** Lighten a hex colour toward white by `amount` (0-1), for bar-top gradients. */
function lighten(hex: string, amount: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const int = parseInt(full, 16);
    // eslint-disable-next-line no-bitwise
    const r = (int >> 16) & 255;
    // eslint-disable-next-line no-bitwise
    const g = (int >> 8) & 255;
    // eslint-disable-next-line no-bitwise
    const b = int & 255;
    const mix = (c: number) => Math.round(c + (255 - c) * amount);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

/** Subtle within-hue vertical gradient: lighter at the top, full colour at the base. */
function barGradient(color: string) {
    return {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
            { offset: 0, color: lighten(color, 0.35) },
            { offset: 1, color },
        ],
    };
}

export interface LineSeriesSpec {
    name: string;
    data: (number | null)[];
    color?: string;
    /** Gradient area wash under the line — the lead series only. */
    area?: boolean;
    /** Dash pattern, e.g. [2, 5] for an in-progress tail. */
    dash?: number[];
    /** Dimmed, for in-progress "partial" tails. */
    faded?: boolean;
    showSymbol?: boolean;
    tooltip?: boolean;
    /** Stroke width in px — the lead series of a locked chart carries extra weight. Defaults to 2.5. */
    lineWidth?: number;
    markLine?: unknown;
    markArea?: unknown;
}

/**
 * A magnitude layer drawn *behind* the lines on its own axis — counts sitting
 * under percentages, where a shared scale would flatten one of them. Rendered
 * as a low-alpha wash so the lines stay the foreground; per-point colours let
 * the caller dim an in-progress bar.
 */
export interface VolumeBarSpec {
    name: string;
    data: (number | null)[];
    /** One fill per point, so a partial day can read as lighter than the rest. */
    colors: string[];
    axisColor: string;
    valueFormatter?: ValueFormatter;
}

/**
 * A thin stepped count line, scaled onto its own hidden axis so it hugs the
 * bottom of the plot rather than competing with the percentage lines or the
 * volume bars — e.g. the number of jobs active that day (deepcontext-b292),
 * where the point is a glanceable shape ("did the job count drop?"), not a
 * value read off a scale.
 */
export interface StepIndicatorSpec {
    name: string;
    data: (number | null)[];
    color: string;
}

export interface LineOptionArgs {
    categories: string[];
    series: LineSeriesSpec[];
    /**
     * One or more magnitude layers, each on its own right-hand axis (offset
     * outward so their labels don't overlap) — e.g. bid requests and matched
     * URLs sit a couple of orders of magnitude apart and can't share a scale.
     * Bars for every layer are drawn side by side, grouped by day.
     */
    volumes?: VolumeBarSpec[];
    /** A single thin stepped count line, e.g. active-jobs-per-day. */
    stepIndicator?: StepIndicatorSpec;
    palette: ChartPalette;
    valueFormatter?: ValueFormatter;
    axisLabelFormatter?: (label: string) => string;
    tooltipFormatter?: (params: unknown) => string;
    /** Force the legend on/off; by default it appears for two or more series. */
    showLegend?: boolean;
    yMax?: number;
    yMin?: number;
    grid?: Record<string, number | boolean>;
}

// Every extra right-hand axis beyond the first is offset outward by this
// many px so its labels clear the axis before it (see the volumes.map below).
// ECharts' containLabel already measures each axis's real rendered label —
// offset included — and grows the grid to fit it, so this offset alone is
// what makes room for a second/third column; grid.right only needs a small
// flat edge gutter on top. Anything more double-reserves and leaves a dead
// band right of the outermost column — the bug this fixes (deepcontext-m282).
export const RIGHT_AXIS_COLUMN_OFFSET = 52;
const RIGHT_AXIS_EDGE_GUTTER = 8;
// Small, deliberately NOT RIGHT_AXIS_COLUMN_OFFSET-sized — that offset is
// already reserved via each axis's own `offset` property (see
// rightAxisOffset), which containLabel measures on its own. Adding the full
// step again here would double-reserve it and reopen the dead band this fix
// removes; this only covers the sliver of extra edge breathing room an extra
// stacked column reasonably wants.
const RIGHT_AXIS_EDGE_GUTTER_PER_EXTRA_COLUMN = 4;

/** Px offset for the `index`-th (0-based) stacked right-hand axis. */
export function rightAxisOffset(index: number): number {
    return Math.max(0, index) * RIGHT_AXIS_COLUMN_OFFSET;
}

/** A right-hand value axis's axisLabel — same shape everywhere so every stacked column reads consistently. */
export function pinnedRightAxisLabel(format: (value: number) => string, color: string) {
    return { color, formatter: (value: number) => format(value) };
}

/**
 * Px to reserve on a chart grid's right edge for a chart carrying `columns`
 * stacked right-hand axis columns. Shared by every chart option builder that
 * places axes there, so two charts asked for the same column count resolve
 * to (approximately — ECharts' containLabel still adds its own per-chart
 * measurement of each axis's real label text on top of this) the same
 * plot-area right edge, instead of each hand-picking its own unrelated
 * number (deepcontext-m282). The bulk of the reservation for columns beyond
 * the first comes from `rightAxisOffset`, which containLabel measures into
 * its own sizing; this is just the flat edge gutter beyond that.
 */
export function rightAxisInset(columns: number): number {
    return RIGHT_AXIS_EDGE_GUTTER + (Math.max(1, columns) - 1) * RIGHT_AXIS_EDGE_GUTTER_PER_EXTRA_COLUMN;
}

/** Smooth 2.5px lines; the lead series carries a gradient area. */
export function smoothLineOption({
    categories,
    series,
    volumes = [],
    stepIndicator,
    palette,
    valueFormatter = identityFormat,
    axisLabelFormatter,
    tooltipFormatter,
    showLegend,
    yMax,
    yMin,
    grid,
}: LineOptionArgs): EChartsCoreOption {
    const legendNames = series.filter((s) => s.tooltip !== false).map((s) => s.name);
    const legendVisible = showLegend ?? legendNames.length > 1;
    // Each volume layer draws its own right-hand axis, so the plot area
    // shrinks by one stacked label column per layer.
    const rightPadding = rightAxisInset(volumes.length);

    return {
        color: palette.categorical,
        animation: false,
        grid: { left: 8, right: rightPadding, top: legendVisible ? 36 : 16, bottom: 4, containLabel: true, ...grid },
        legend: legendVisible
            ? { data: legendNames, top: 0, left: 0, textStyle: { color: palette.chrome.inkMuted } }
            : { show: false },
        tooltip: {
            ...baseTooltip(palette, 'axis'),
            ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
            valueFormatter: tooltipFormatter ? undefined : (value: number) => valueFormatter(value),
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: categories,
            axisLabel: axisLabelFormatter ? { formatter: axisLabelFormatter } : {},
        },
        yAxis: [
            {
                type: 'value',
                max: yMax,
                min: yMin,
                axisLabel: { formatter: (value: number) => valueFormatter(value) },
            },
            // One scale per volume layer, each carrying its own units on the
            // right, offset outward so their labels don't collide — none draw
            // gridlines of their own, so one set of horizontal rules keeps the
            // plot readable.
            ...volumes.map((volume, i) => ({
                type: 'value',
                position: 'right',
                offset: rightAxisOffset(i),
                splitLine: { show: false },
                axisLabel: pinnedRightAxisLabel(volume.valueFormatter ?? identityFormat, volume.axisColor),
            })),
            // Hidden axis for the step indicator, scaled to a multiple of its
            // own max so the line sits low in the plot rather than spanning
            // its full height — a glanceable shape, not a value read off a
            // scale (hence no label/gridline of its own).
            ...(stepIndicator
                ? [
                      {
                          type: 'value',
                          show: false,
                          min: 0,
                          max: Math.max(1, ...stepIndicator.data.map((v) => v ?? 0)) * 4,
                          splitLine: { show: false },
                      },
                  ]
                : []),
        ],
        series: [
            ...volumes.map((volume, i) => ({
                type: 'bar',
                name: volume.name,
                yAxisIndex: 1 + i,
                data: volume.data.map((value, j) => ({
                    value,
                    itemStyle: { color: volume.colors[j] },
                })),
                barMaxWidth: 24,
                barCategoryGap: '30%',
                itemStyle: { borderRadius: [4, 4, 0, 0] },
                // Behind every line, including the faded partial tails.
                z: 1,
                silent: true,
            })),
            ...(stepIndicator
                ? [
                      {
                          type: 'line',
                          name: stepIndicator.name,
                          data: stepIndicator.data,
                          step: 'end',
                          symbol: 'none',
                          showSymbol: false,
                          connectNulls: false,
                          lineStyle: { width: 1.5, color: stepIndicator.color },
                          itemStyle: { color: stepIndicator.color },
                          tooltip: { show: false },
                          silent: true,
                          yAxisIndex: 1 + volumes.length,
                          z: 2,
                      },
                  ]
                : []),
            ...series.map((s, i) => ({
                type: 'line',
                name: s.name,
                data: s.data,
                smooth: true,
                smoothMonotone: 'x',
                connectNulls: false,
                showSymbol: s.showSymbol ?? false,
                symbolSize: 8,
                emphasis: { focus: 'series' },
                lineStyle: {
                    width: s.lineWidth ?? 2.5,
                    cap: 'round',
                    join: 'round',
                    opacity: s.faded ? 0.45 : 1,
                    ...(s.dash ? { type: s.dash as unknown as string } : {}),
                },
                itemStyle: { color: s.color ?? palette.categorical[i % palette.categorical.length] },
                areaStyle: s.area ? { color: areaGradient(s.color ?? palette.categorical[i % palette.categorical.length]) } : undefined,
                tooltip: s.tooltip === false ? { show: false } : undefined,
                markLine: s.markLine,
                markArea: s.markArea,
                z: s.faded ? 2 : 3,
                yAxisIndex: 0,
                })),
        ],
    };
}

export interface DonutSlice {
    name: string;
    value: number;
    color?: string;
}

export interface DonutOptionArgs {
    slices: DonutSlice[];
    palette: ChartPalette;
    /** Centre KPI: the big number and the word under it. */
    centerValue?: string;
    centerLabel?: string;
    valueFormatter?: ValueFormatter;
    showLegend?: boolean;
    radius?: [string, string];
    /** A faint full-circle ring behind the segments, revealed through their padAngle gaps. */
    track?: boolean;
    /** Rendered chart height in px — the centre KPI's font sizes, and whether
     * its subtext line is shown at all, are derived from this against `radius`
     * so the KPI never overflows the donut's hole at small sizes. */
    height?: number;
}

const CENTER_VALUE_FONT_RANGE: [number, number] = [12, 22];
const CENTER_SUBTEXT_FONT_RANGE: [number, number] = [8, 11];
// Below this inner-radius (px), the subtext line can't fit above/below the
// value line without touching the ring, so it's dropped entirely.
const MIN_INNER_RADIUS_FOR_SUBTEXT = 30;

function clampFont(px: number, [min, max]: [number, number]): number {
    return Math.max(min, Math.min(max, Math.round(px)));
}

/** Derives the centre-KPI font sizes (and subtext fit) from the rendered inner radius. */
function centerKpiStyle(height: number, innerRadiusPercent: string) {
    const innerRadiusPx = (height / 2) * (parseFloat(innerRadiusPercent) / 100);
    return {
        valueFontSize: clampFont(innerRadiusPx * 0.34, CENTER_VALUE_FONT_RANGE),
        subtextFontSize: clampFont(innerRadiusPx * 0.16, CENTER_SUBTEXT_FONT_RANGE),
        showSubtext: innerRadiusPx >= MIN_INNER_RADIUS_FOR_SUBTEXT,
    };
}

/** Rounded, padded donut with a centre KPI. */
export function donutOption({
    slices,
    palette,
    centerValue,
    centerLabel,
    valueFormatter = identityFormat,
    showLegend = false,
    radius = ['62%', '88%'],
    track = false,
    height = 220,
}: DonutOptionArgs): EChartsCoreOption {
    const center: [string, string] = ['50%', showLegend ? '45%' : '50%'];
    const { valueFontSize, subtextFontSize, showSubtext } = centerKpiStyle(height, radius[0]);
    return {
        color: palette.categorical,
        animation: false,
        tooltip: {
            ...baseTooltip(palette, 'item'),
            formatter: (params: { name: string; value: number; percent: number }) =>
                `${params.name}<br/><strong>${valueFormatter(params.value)}</strong> · ${params.percent}%`,
        },
        legend: showLegend
            ? {
                  bottom: 0,
                  icon: 'circle',
                  itemWidth: 8,
                  itemHeight: 8,
                  textStyle: { color: palette.chrome.inkMuted, fontSize: 11 },
              }
            : { show: false },
        series: [
            // Faint full-circle track, drawn first so it sits behind the ring —
            // its colour shows through the ring's padAngle gaps.
            ...(track
                ? [
                      {
                          type: 'pie' as const,
                          radius,
                          center,
                          silent: true,
                          label: { show: false },
                          labelLine: { show: false },
                          itemStyle: { color: palette.chrome.gridline },
                          data: [{ value: 1, name: '' }],
                      },
                  ]
                : []),
            {
                type: 'pie',
                radius,
                center,
                avoidLabelOverlap: true,
                padAngle: 2,
                label: { show: false },
                labelLine: { show: false },
                emphasis: { scaleSize: 4, label: { show: false } },
                itemStyle: {
                    borderRadius: 8,
                    borderColor: palette.chrome.surface,
                    borderWidth: 2,
                },
                data: slices.map((slice, i) => ({
                    name: slice.name,
                    value: slice.value,
                    itemStyle: { color: slice.color ?? palette.categorical[i % palette.categorical.length] },
                    // `position: 'center'` is pie's own built-in centre-label
                    // placement — it's pinned to the series' true centre by
                    // ECharts' pie layout itself, not by a separately-anchored
                    // title/graphic element that can drift off it. Attaching it
                    // to one slice is enough: 'center' ignores that slice's own
                    // wedge and always resolves to the whole pie's centre. It's
                    // repeated under `emphasis` so hovering that first slice
                    // doesn't blank the KPI (the series sets emphasis.label.show
                    // to false for every other slice's hover label).
                    ...(i === 0 && centerValue
                        ? (() => {
                              const centerLabelOption = {
                                  show: true,
                                  position: 'center' as const,
                                  formatter: () =>
                                      showSubtext
                                          ? `{value|${centerValue}}\n{label|${centerLabel}}`
                                          : `{value|${centerValue}}`,
                                  rich: {
                                      value: {
                                          fontSize: valueFontSize,
                                          fontWeight: 700,
                                          color: palette.chrome.ink,
                                          align: 'center' as const,
                                          lineHeight: Math.round(valueFontSize * 1.2),
                                          textBorderWidth: 0,
                                      },
                                      label: {
                                          fontSize: subtextFontSize,
                                          color: palette.chrome.inkMuted,
                                          align: 'center' as const,
                                          lineHeight: Math.round(subtextFontSize * 1.6),
                                          textBorderWidth: 0,
                                      },
                                  },
                              };
                              return {
                                  label: centerLabelOption,
                                  emphasis: { label: centerLabelOption },
                              };
                          })()
                        : null),
                })),
            },
        ],
    };
}

export interface StackedBarSeriesSpec {
    name: string;
    data: number[];
    color?: string;
}

export interface StackedBarOptionArgs {
    categories: string[];
    series: StackedBarSeriesSpec[];
    palette: ChartPalette;
    valueFormatter?: ValueFormatter;
    axisLabelFormatter?: (label: string) => string;
}

/** Pill-capped stacked columns, segments separated by 2px surface gaps. */
export function stackedBarOption({
    categories,
    series,
    palette,
    valueFormatter = identityFormat,
    axisLabelFormatter,
}: StackedBarOptionArgs): EChartsCoreOption {
    const last = series.length - 1;

    return {
        color: palette.categorical,
        animation: false,
        grid: { left: 8, right: 12, top: series.length > 1 ? 36 : 16, bottom: 4, containLabel: true },
        legend:
            series.length > 1
                ? { data: series.map((s) => s.name), top: 0, left: 0, textStyle: { color: palette.chrome.inkMuted } }
                : { show: false },
        tooltip: {
            // Shadow axis pointer washes the whole hovered column, not just a
            // hairline — the "column hover wash" this chart's design calls for.
            ...baseTooltip(palette, 'axis', 'shadow'),
            valueFormatter: (value: number) => valueFormatter(value),
        },
        xAxis: {
            type: 'category',
            data: categories,
            axisLabel: axisLabelFormatter ? { formatter: axisLabelFormatter } : {},
        },
        yAxis: { type: 'value', axisLabel: { formatter: (value: number) => valueFormatter(value) } },
        series: series.map((s, i) => ({
            type: 'bar',
            name: s.name,
            stack: 'total',
            data: s.data,
            barMaxWidth: 24,
            barCategoryGap: '38%',
            emphasis: { focus: 'series' },
            itemStyle: {
                // Subtle within-hue vertical gradient, lighter at the top.
                color: barGradient(s.color ?? palette.categorical[i % palette.categorical.length]),
                // Pill cap on the top of the stack, square at the baseline; the
                // 2px surface border is the gap between touching segments.
                borderRadius: series.length === 1 ? [10, 10, 0, 0] : i === last ? [10, 10, 0, 0] : 0,
                borderColor: palette.chrome.surface,
                borderWidth: 2,
            },
        })),
    };
}

export interface DualPanelSeriesSpec {
    name: string;
    data: (number | null)[];
    /** Line + area colour — the lead hue for this panel. */
    color: string;
    valueFormatter?: ValueFormatter;
}

export interface DualPanelAreaOptionArgs {
    categories: string[];
    /** Exactly two panels, stacked top to bottom. */
    panels: [DualPanelSeriesSpec, DualPanelSeriesSpec];
    palette: ChartPalette;
    axisLabelFormatter?: (label: string) => string;
}

/**
 * Two slim stacked panels sharing the x time axis, each with its own labeled
 * value scale. For measures a magnitude apart (impressions vs URL counts) a
 * single shared axis flattens the smaller series into noise; two grids in one
 * instance keep both readable while still sharing one synced crosshair
 * (`axisPointer.link`) instead of two independently-hovering charts.
 */
export function dualPanelAreaOption({
    categories,
    panels,
    palette,
    axisLabelFormatter,
}: DualPanelAreaOptionArgs): EChartsCoreOption {
    const gridTop = ['10%', '58%'];
    const gridHeight = ['32%', '32%'];

    return {
        animation: false,
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        tooltip: {
            ...baseTooltip(palette, 'axis'),
        },
        title: panels.map((panel, i) => ({
            text: panel.name,
            left: 4,
            top: i === 0 ? 0 : '46%',
            textStyle: { fontSize: 10, fontWeight: 700, color: palette.chrome.inkMuted },
        })),
        grid: panels.map((_, i) => ({
            left: 48,
            right: 12,
            top: gridTop[i],
            height: gridHeight[i],
            containLabel: false,
        })),
        xAxis: panels.map((_, i) => ({
            type: 'category',
            gridIndex: i,
            data: categories,
            boundaryGap: false,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                show: i === panels.length - 1,
                color: palette.chrome.inkMuted,
                fontSize: 10,
                formatter: axisLabelFormatter,
            },
        })),
        yAxis: panels.map((panel, i) => ({
            type: 'value',
            gridIndex: i,
            splitLine: { show: false },
            axisLabel: {
                color: palette.chrome.inkMuted,
                fontSize: 10,
                formatter: (value: number) => (panel.valueFormatter ?? identityFormat)(value),
            },
        })),
        series: panels.map((panel, i) => ({
            type: 'line',
            name: panel.name,
            data: panel.data,
            gridIndex: i,
            xAxisIndex: i,
            yAxisIndex: i,
            smooth: true,
            smoothMonotone: 'x',
            showSymbol: false,
            lineStyle: { width: 2.2, cap: 'round', join: 'round', color: panel.color },
            itemStyle: { color: panel.color },
            areaStyle: { color: areaGradient(panel.color) },
            z: 3,
        })),
    };
}

export interface HorizontalBarOptionArgs {
    categories: string[];
    values: number[];
    palette: ChartPalette;
    /** Per-bar colour; defaults to the slot-1 hue for every bar (nominal data). */
    colors?: string[];
    valueFormatter?: ValueFormatter;
    axisNameWidth?: number;
}

/** Single-hue horizontal bars, 4px rounded data end, square at the baseline. */
export function horizontalBarOption({
    categories,
    values,
    palette,
    colors,
    valueFormatter = identityFormat,
    axisNameWidth = 96,
}: HorizontalBarOptionArgs): EChartsCoreOption {
    return {
        color: palette.categorical,
        animation: false,
        grid: { left: 4, right: 24, top: 8, bottom: 4, containLabel: true },
        tooltip: {
            ...baseTooltip(palette, 'item'),
            formatter: (params: { name: string; value: number }) =>
                `${params.name}<br/><strong>${valueFormatter(params.value)}</strong>`,
        },
        xAxis: { type: 'value', axisLabel: { formatter: (value: number) => valueFormatter(value) } },
        yAxis: {
            type: 'category',
            data: categories,
            inverse: true,
            axisLabel: { width: axisNameWidth, overflow: 'truncate', color: palette.chrome.inkMuted },
        },
        series: [
            {
                type: 'bar',
                data: values.map((value, i) => ({
                    value,
                    itemStyle: { color: colors?.[i] ?? palette.categorical[0] },
                })),
                barMaxWidth: 18,
                barCategoryGap: '38%',
                itemStyle: { borderRadius: [0, 4, 4, 0] },
                emphasis: { itemStyle: { opacity: 0.85 } },
                cursor: 'pointer',
            },
        ],
    };
}
